import { useEffect, useState } from "react"
import { NavLink } from "react-router-dom"

import DeleteButton from "./DeleteButton"
import IndentedText from "./IndentedText"
import Checkbox from "./Checkbox"
import DropDown from "./DropDown"
import TextArea from "./TextArea"
import TextInput from "./TextInput"
import ItemButtons from "./ItemButtons"
import ComputedField from "./ComputedField"
import ComputedSelect from "./ComputedSelect"

const noop = (i) => console.log("noop", i)


const InlineEditor = ({
    heading,
    records=[],
    fields=[
        {name: "id", label: "ID", colWidthPct: 46, widget: null},
        {name: "label", label: "Label", colWidthPct: 54, widget: null},
    ],
    keyField=true,
    allowChangeOrder=true,
    onMoveUp=noop,
    onMoveDown=noop,
    allowChangeIndent=false,
    changeIndentAttr=null,
    onMoveLeft=noop,
    onMoveRight=noop,
    editLink=true,
    editBaseURL,
    allowDelete=true,
    onDelete=noop,
    children
}) => {
    const [columns, setColumns] = useState(null)

    const getWidget = (entity, fld) => {
        switch(fld.widget) {
            case "smallText":
                return (<span style={{fontSize: "smaller"}}>{entity[fld.name]}</span>)
            case "text":
                return (
                    <TextInput
                        entity={entity}
                        fld={fld}
                        indentAttr={changeIndentAttr}
                    />
                )
            case "indentedText":
                return (
                    <IndentedText
                        rec={entity}
                        fld={fld}
                        indentAttr={changeIndentAttr}
                    />
                )
            case "select":
                return (
                    <DropDown
                        entity={entity}
                        fld={fld}
                    />
                )
            case "computedField":
                return (
                    <ComputedField
                        entity={entity}
                        fld={fld}
                    />
                )
            case "textArea":
                return (
                    <TextArea
                        entity={entity}
                        fld={fld}
                    />
                )
            case "checkbox":
                return (
                    <Checkbox
                        entity={entity}
                        fld={fld}
                    />
                )
            case "computedSelect":
                return (
                    <ComputedSelect
                        entity={entity}
                        fld={fld}
                    />
                )
            default:
                return entity[fld.name]
        }
    }
    useEffect(
        () => {
            setColumns(null)
        }, [heading]
    )
    useEffect(
        () => {
            if (!columns) {
                let changeCol = 0
                let editCol = 0
                let deleteCol = 0
                if (allowChangeOrder) {
                    changeCol = 3
                    if (allowChangeIndent) {
                        changeCol = 6
                    }
                }
                if (editLink) {
                    editCol = 4
                }
                if (allowDelete) {
                    deleteCol = 1
                }
                const dfltFldsPct = changeCol + editCol + deleteCol
                const remainingPct = 100 - dfltFldsPct
                // console.log(`changeCol: ${changeCol}, editCol: ${editCol} deleteCol: ${deleteCol} remainingPct: ${remainingPct}`)
                const numFlds = fields.length
                const fieldsWithWidths = fields.filter(f => undefined !== f.colWidthPct).map(f => f.colWidthPct)
                const numFldsWithWidths = fieldsWithWidths.length
                let desiredWidth = 0
                if (numFldsWithWidths > 0) {
                    desiredWidth = fieldsWithWidths.reduce((prevVal, currVal) => prevVal + currVal, 0)
                }
                // console.log(`numFlds: ${numFlds} numFldsWithWidths: ${numFldsWithWidths} desiredWidth: ${desiredWidth}`)
                const cols = []
                if (changeCol > 0) {
                    cols.push({label: "", width: `${changeCol}%`})
                }
                if (editCol > 0) {
                    cols.push({label: "", width: `${editCol}%`})
                }
                let dfltColWidthPct = 0
                if (numFldsWithWidths < numFlds) {
                    const numMissingWidths = numFlds - numFldsWithWidths
                    const remUnnormalizedWidth = 100 - desiredWidth
                    dfltColWidthPct = remUnnormalizedWidth / numMissingWidths
                }
                fields.forEach(fld => {
                    // console.log(fld)
                    let {label, colWidthPct} = fld
                    if (colWidthPct === undefined) {
                        colWidthPct = dfltColWidthPct
                    }
                    const normWidth = 0.01 * remainingPct * colWidthPct
                    cols.push({label, width: `${normWidth}%`})
                })
                if (deleteCol > 0) {
                    cols.push({label: "", width: `${deleteCol}%`})
                }
                // console.log(cols)
                setColumns(cols)
            }

        }, [columns, allowChangeOrder, allowChangeIndent, editLink, allowDelete, fields]
    )
    if (!columns) {
        return <div>loading...</div>
    }
    return (
        <div className="inline-editor">
            <fieldset>
                <legend>{heading}</legend>
                {
                    records.length === 0
                    ?
                        <p>There currently aren't any {heading.toLowerCase()}</p>
                    :
                        <table>
                            <colgroup>
                                {columns.map((c, i) =>
                                    <col
                                        key={i}
                                        span="1"
                                        style={{width: c.width}}
                                    />
                                )}
                            </colgroup>
                            <thead>
                                <tr>
                                    {columns.map((c, i) =>
                                        <th key={i}> {c.label} </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((rec, i, arr) =>
                                    <tr key={(keyField) ? rec.id : i}>
                                        {allowChangeOrder &&
                                            <td style={{borderBottom: "1px solid black"}}>
                                                <ItemButtons
                                                    n={i}
                                                    of={arr.length-1}
                                                    depth={rec[changeIndentAttr] ? rec[changeIndentAttr] : 1}
                                                    allowChangeIndent={allowChangeIndent}
                                                    onMoveUp={onMoveUp}
                                                    onMoveDown={onMoveDown}
                                                    onMoveLeft={onMoveLeft}
                                                    onMoveRight={onMoveRight}
                                                />
                                        </td>
                                        }
                                        {editLink &&
                                            <td style={{borderBottom: "1px solid black"}}>
                                                <NavLink to={`${editBaseURL}/${rec.id}`} >
                                                    edit
                                                </NavLink>
                                            </td>
                                        }
                                        {fields.map(fld =>
                                            <td
                                                key={fld.name}
                                                style={{borderBottom: "1px solid black"}}
                                            >
                                                {getWidget(rec, fld)}
                                            </td>
                                        )}
                                        {allowDelete &&
                                            <td style={{borderBottom: "1px solid black"}}>
                                                <DeleteButton item={rec} onDelete={onDelete} />
                                            </td>
                                        }
                                    </tr>
                                )}
                            </tbody>
                        </table>
                }
                {children}
            </fieldset>
        </div>
    )
}

export default InlineEditor